import { useEffect, useMemo, useRef, useState } from "react";
import Chatbot from "@yoctotta/chatbot";
import "@yoctotta/chatbot/dist/chatbot.css";
import { Box, Typography, useTheme } from "@mui/material";
import { kamanService } from "../../utils/services/kamanService";
import { forecastDataService } from "../../utils/services/forecast-data.service";
import config from "../../config";

// How long `runForecast` waits before deciding the submission is genuinely
// running in the background (vs. an immediate validation failure it should
// report right away, e.g. the form isn't ready).
const RUN_FORECAST_FAST_FAIL_WINDOW_MS = 1200;

/**
 * The forecast-planning AI agent — its own Kaman expert
 * (`kaman_expert_forecast_planner`), separate from `kaman_expert_forecast`
 * (pages/forecast/ForecastAgent). Three tools:
 *  - `draftForecast` hands a plan back to the host via `onDraftForecast(draft)`,
 *    which pours it into ForecastParamsPanel's form. Never submits anything.
 *  - `runForecast` calls `onRunForecast()`, the exact same submit path as a
 *    human clicking "Run Forecast" on whatever's currently in the form. The
 *    actual run (kaman workflow + server-side polling) can take a couple of
 *    minutes, so this tool does NOT wait for it — it kicks the run off,
 *    tracks it in `runStateRef`, and returns almost immediately (fast-failing
 *    only on an instant validation error). Only call this after the user has
 *    explicitly asked to run/submit it.
 *  - `getForecastResults` checks `runStateRef` for that in-flight run: if
 *    it's still going, says so; once done, fetches the completed
 *    forecast-data doc(s) and hands them back as plain data so the model can
 *    build a visualization artifact from them (HTML preferred, PDF as a
 *    fallback) via Kaman's own native artifact/canvas capability, using its
 *    own creative layout but only ever the real data points returned here.
 *    (Deliberately NOT named "createArtifact" — Kaman has its own built-in
 *    artifact-creation concept with that name, e.g. a `document` artifact
 *    needs a `name`/`url` — reusing that name here made the model conflate
 *    the two and hallucinate a `name` argument this tool never asked for.)
 *
 * `chatHeight` lets the host size the chat window to whatever space it
 * actually has (e.g. the "Custom" drawer on Planning > Forecast).
 */
// HTML the insight workflow wrote → plain text points for the agent.
const plainText = (html) =>
  String(html || "")
    .replace(/<\/(li|p|div|h\d)>/gi, "\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .split("\n")
    .map((t) => t.trim())
    .filter(Boolean);

/**
 * One finished forecast run, as getForecastResults hands it to the agent:
 * what's on the page — forecast with its likely range and any event uplift,
 * how it was made, the reorder, the inventory ledger, the warehouse split and
 * the DropSkip Insight — compact, and real sales kept apart from estimates.
 */
export const resultForAgent = (d) => {
  const model = d.forecast_model || {};
  const fill = model.sales_fill;
  const history = Array.isArray(d.historical_data) ? d.historical_data : [];
  return {
    sku: d.sku,
    product_name: d.input?.product?.name || null,
    forecast_by: d.input?.forecast_by,
    horizon: d.input?.horizon,
    type: d.input?.type,
    run_at: d.createdAt,
    forecast_adjusted: (d.forecast_adjusted || []).map((f) => ({
      date: f.date,
      predicted_qty: f.predicted_qty,
      low: f.lower ?? null,
      high: f.upper ?? null,
      ...(f.event_uplift ? { event_uplift: f.event_uplift, before_uplift: f.base_qty } : {}),
    })),
    sales_history: history
      .filter((h) => !h.filled)
      .map((h) => ({ date: h.ds || h.date, units: h.value ?? h.quantity })),
    ...(fill?.applied && {
      estimated_history: {
        note: "Months before the first sale estimated by Sales Fill — not real sales.",
        periods: fill.periods,
        from: fill.from,
        to: fill.to,
        sources: (fill.sources || []).map((s) => ({ from: s.source, label: s.label, scale_pct: Math.round((s.scale || 0) * 1000) / 10 })),
      },
    }),
    method: model.model
      ? {
          name: model.model_label || model.model,
          demand_type: model.demand_type || null,
          tested_error_pct: model.tested_error?.wape ?? null,
          beat_same_as_last_year_pct: model.beat_benchmark_pct ?? null,
          likely_range_level_pct: model.interval_level ?? null,
          warnings: model.warnings || [],
        }
      : null,
    metrics: d.metrics,
    insight: plainText(d.insight),
    insight_status: d.insight_status || (d.insight ? "ready" : null),
    explanation: plainText(d.explanation),
    warehouse_split: (d.allocation?.warehouses || []).map((w) => ({
      warehouse: w.name,
      share_pct: Math.round((Number(w.pct) || 0) * 1000) / 10,
      forecast_units: w.forecast_units,
    })),
    inventory_ledger: d.ledger
      ? {
          stock_as_of: d.ledger.stock_as_of,
          lead_time_days: d.ledger.lead_time_days,
          safety_stock: d.ledger.safety_stock,
          waiting_to_ship: d.ledger.waiting_to_ship || null,
          months: (d.ledger.months || []).map((m) => ({
            month: m.month,
            kind: m.kind,
            on_hand: m.on_hand,
            sales: m.sales,
            forecast: m.forecast,
            po_inbound: m.po_inbound ?? m.po_received ?? null,
            shortfall: m.shortfall || null,
            recommended_order_qty: m.reorder_qty || null,
            recommended_order_arrives: m.reorder_arrives || null,
            days_out_of_stock: m.stockout?.days || null,
          })),
        }
      : null,
  };
};

const ForecastPlannerAgent = ({
  chatHeight = "calc(100vh - 164px)",
  onDraftForecast,
  onRunForecast,
}) => {
  const [kamanToken, setKamanToken] = useState("");

  // Tracks the most recently kicked-off runForecast call across tool calls
  // (a plain ref, not state — nothing here should trigger a re-render).
  // status: "idle" | "running" | "done" | "error"
  const runStateRef = useRef({ status: "idle", ids: null, error: null });

  const theme = useTheme();
  const defaultColor = "#D13438";
  const primary_color = theme?.palette?.primary?.main || defaultColor;
  const secondary_color = theme?.palette?.secondary?.main || defaultColor;

  // Schema for `draftForecast` — mirrors the request body
  // ForecastParamsPanel.handleSubmit builds, so a drafted plan slots
  // straight into that form's fields.
  const forecastDraftInputFields = useMemo(
    () => [
      {
        name: "items",
        type: "array",
        description:
          "One entry per forecast target. Every entry must share the same forecast_by scope (SKU, Product, Category, or Product Type) — make a separate draftForecast call if the user wants to mix scopes.",
        required: true,
        innerFields: {
          type: "object",
          innerFields: [
            {
              name: "forecast_by",
              type: "string",
              description:
                "How this item's target is expressed — exactly one of: SKU, Product, Category, Product Type.",
              required: true,
            },
            {
              name: "sku",
              type: "string",
              description:
                "Exact SKU code, resolved via rl_products. Required when forecast_by is SKU.",
              required: false,
            },
            {
              name: "product",
              type: "string",
              description:
                "The product's _id, resolved via rl_products by name — never invent one. Required when forecast_by is SKU or Product.",
              required: false,
            },
            {
              name: "product_name",
              type: "string",
              description:
                "Optional: the product's display name, if already resolved via rl_products. Used only to label the drafted form nicely — not required.",
              required: false,
            },
            {
              name: "category",
              type: "string",
              description:
                "Exact category name, resolved via rl_products.category. Required when forecast_by is Category.",
              required: false,
            },
            {
              name: "product_type",
              type: "string",
              description:
                "Exact product_type value from rl_products. Required when forecast_by is Product Type.",
              required: false,
            },
            {
              name: "horizon",
              type: "number",
              description: "Number of periods to forecast forward, e.g. 3.",
              required: true,
            },
            {
              name: "type",
              type: "string",
              description:
                "Time bucket for horizon — exactly one of: Daily, Weekly, Monthly.",
              required: true,
            },
            {
              name: "warehouse",
              type: "array",
              description:
                "Warehouse _ids from rl_warehouses to scope this forecast to — never invent one. Omit entirely to cover every warehouse.",
              required: false,
              innerFields: { type: "string" },
            },
            {
              name: "warehouse_names",
              type: "array",
              description:
                "Optional: display names for `warehouse`, same order/length, if already resolved via rl_warehouses. Not required.",
              required: false,
              innerFields: { type: "string" },
            },
            {
              name: "channel",
              type: "array",
              description:
                "Sales channel names to scope this forecast to (e.g. Shopify, Amazon). Omit entirely to cover every channel.",
              required: false,
              innerFields: { type: "string" },
            },
            {
              name: "discount",
              type: "number",
              description:
                "Planned promotional discount percentage to factor in. 0 if the user didn't mention one.",
              required: false,
            },
            {
              name: "lead_time_days",
              type: "number",
              description:
                "Supplier lead time in days, used for reorder point / safety stock math.",
              required: false,
            },
            {
              name: "safety_stock_percentage",
              type: "number",
              description:
                "Safety stock target as a percentage of on-hand quantity.",
              required: false,
            },
            {
              name: "min_coverage_days",
              type: "number",
              description:
                "Minimum days of demand the reorder must cover. Omit unless the user asked for it (default 0).",
              required: false,
            },
            {
              name: "min_order_qty",
              type: "number",
              description:
                "Minimum order quantity (MOQ) per reorder. Omit to use each SKU's own MOQ.",
              required: false,
            },
          ],
        },
      },
      {
        name: "forecast_type",
        type: "string",
        description:
          'Either "warehouse" (default) or "channel". Stays "warehouse" unless the user explicitly asked to forecast BY sales channel — picking specific channels as a filter on an otherwise warehouse-scoped forecast is NOT the same thing and should NOT set this to "channel". Omit entirely to use the default.',
        required: false,
      },
      {
        name: "history_start",
        type: "string",
        description:
          'Sales History From: the first month of sales history to learn from, as "YYYY-MM". At most 36 months back and at least one full month before the current month. Omit to use the default (as far back as allowed). Only set it when the user asks to start the history later (e.g. "only use sales since 2025").',
        required: false,
      },
      {
        name: "event_uplifts",
        type: "array",
        description:
          "Expected extra sales on event days the history can't show (e.g. a Black Friday the SKU wasn't selling on yet). Added on top of the forecast and shown separately; its accuracy isn't tested. Only include events the user asked for, with the lift % they gave — never invent a lift.",
        required: false,
        innerFields: {
          type: "object",
          innerFields: [
            {
              name: "name",
              type: "string",
              description:
                'Event name, e.g. "Black Friday – Cyber Monday" or the user\'s own (e.g. "TikTok live sale").',
              required: true,
            },
            {
              name: "preset",
              type: "string",
              description:
                "Optional known event key — bfcm, gifting, prime, valentine, mother, father. With a preset and no dates, its next dates are used.",
              required: false,
            },
            {
              name: "start",
              type: "string",
              description: 'First day, "YYYY-MM-DD". Required unless a preset is given.',
              required: false,
            },
            {
              name: "end",
              type: "string",
              description: 'Last day, "YYYY-MM-DD" (defaults to the start day).',
              required: false,
            },
            {
              name: "lift_pct",
              type: "number",
              description: "Expected change in sales on those days, in %, between -90 and 1000 (e.g. 80 for +80%).",
              required: true,
            },
          ],
        },
      },
      {
        name: "include_lost_sales",
        type: "boolean",
        description:
          "True to adjust demand for stockout/lost-sales periods. Only set true if the user actually asked for this.",
        required: false,
      },
      {
        name: "search_window_days",
        type: "number",
        description:
          "Lookback window in days to search for lost-sales periods. Only meaningful when include_lost_sales is true.",
        required: false,
      },
      {
        name: "demand_window_days",
        type: "number",
        description:
          "Nearest-neighbor window in days used to estimate demand during a lost-sales period. Only meaningful when include_lost_sales is true.",
        required: false,
      },
      {
        name: "dow_adjustment_enabled",
        type: "boolean",
        description:
          "True to weight the lost-sales estimate by day-of-week pattern. Only meaningful when include_lost_sales is true.",
        required: false,
      },
    ],
    [],
  );

  const uiTools = useMemo(
    () => [
      {
        name: "draftForecast",
        description:
          'Fill in the Forecast form with the plan the user just confirmed in chat. This does NOT submit or run anything — it only pre-fills the form so the human can review it and click "Run Forecast" themselves. Call this exactly once per confirmed plan.',
        inputFields: forecastDraftInputFields,
        outputFields: [
          {
            name: "success",
            type: "boolean",
            description: "Whether the form was filled in.",
            required: true,
          },
          {
            name: "message",
            type: "string",
            description: "Human-readable result to relay to the user.",
            required: true,
          },
        ],
        func: async (args) => {
          console.log(args);
          const rawItems = Array.isArray(args?.items) ? args.items : [];
          if (!rawItems.length) {
            return {
              success: false,
              message: "No forecast items were provided — nothing to draft.",
            };
          }
          if (typeof onDraftForecast !== "function") {
            return {
              success: false,
              message:
                "This agent isn't connected to a Forecast form right now.",
            };
          }
          try {
            onDraftForecast({
              items: rawItems,
              forecast_type: args?.forecast_type,
              history_start: args?.history_start,
              event_uplifts: Array.isArray(args?.event_uplifts) ? args.event_uplifts : [],
              include_lost_sales: !!args?.include_lost_sales,
              search_window_days: args?.search_window_days,
              demand_window_days: args?.demand_window_days,
              dow_adjustment_enabled: args?.dow_adjustment_enabled,
            });
            return {
              success: true,
              message:
                'The Forecast form has been filled in with this plan. Tell the user to review it and click "Run Forecast" when ready — you do not submit it yourself.',
            };
          } catch (e) {
            console.error("draftForecast tool failed", e);
            return {
              success: false,
              message: e?.message || "Failed to fill in the forecast form.",
            };
          }
        },
      },
      {
        name: "runForecast",
        description:
          'Start running the forecast currently in the form — the exact same action as the human clicking "Run Forecast" themselves. Takes no arguments: it submits whatever is presently drafted/displayed, not anything you pass in. Only call this after the user has explicitly asked you to run/submit/execute it — never right after draftForecast on your own initiative, and never in the same turn as a fresh draftForecast call for a plan the user hasn\'t yet confirmed. This does NOT wait for the run to finish (it can take a couple of minutes) — it returns as soon as the run has started. Once it returns successfully, say exactly one thing back to the user — tell them the run has started and ask: "If you want to create the artifact let me know once the forecast is generated." That is the ONLY question you ask in that reply — no other question, no other follow-up, and no suggested next actions/quick replies/prompt chips of any kind (e.g. do not suggest exporting to CSV, scheduling another forecast, or anything else) — the reply is the one status sentence plus that one exact question, nothing appended after it. Only call getForecastResults once they\'ve told you the forecast is done and they want the artifact.',
        inputFields: [],
        outputFields: [
          {
            name: "success",
            type: "boolean",
            description: "Whether the forecast run was started.",
            required: true,
          },
          {
            name: "message",
            type: "string",
            description: "Human-readable result to relay to the user.",
            required: true,
          },
        ],
        func: async () => {
          if (typeof onRunForecast !== "function") {
            return {
              success: false,
              message: "This agent isn't connected to a Forecast form right now.",
            };
          }
          if (runStateRef.current.status === "running") {
            return {
              success: false,
              message:
                "A forecast run is already in progress — call getForecastResults to check on it instead of starting another one.",
            };
          }
          runStateRef.current = { status: "running", ids: null, error: null };

          // Kick the run off but don't block this tool call on it — the
          // backend polls kaman synchronously and can take a couple of
          // minutes. Track how it settles in the ref so getForecastResults
          // can pick it up later, whether or not the chat is still open.
          const runPromise = Promise.resolve()
            .then(() => onRunForecast())
            .then((result) => {
              if (result?.success) {
                runStateRef.current = {
                  status: "done",
                  ids: Array.isArray(result.ids) ? result.ids : [],
                  error: null,
                };
              } else {
                runStateRef.current = {
                  status: "error",
                  ids: null,
                  error: result?.message || "The forecast run failed.",
                };
              }
              return result;
            })
            .catch((e) => {
              console.error("runForecast tool failed", e);
              const message = e?.message || "Failed to run the forecast.";
              runStateRef.current = { status: "error", ids: null, error: message };
              return { success: false, message };
            });

          // Give it a brief window to fail fast (form not ready, nothing to
          // submit, etc.) before committing to the "running in the
          // background" story — the real kaman run takes far longer than
          // this and just keeps going regardless.
          const early = await Promise.race([
            runPromise,
            new Promise((resolve) =>
              setTimeout(() => resolve(null), RUN_FORECAST_FAST_FAIL_WINDOW_MS),
            ),
          ]);
          if (early && early.success === false) {
            return early;
          }
          return {
            success: true,
            message:
              "The forecast run has started in the background and can take a couple of minutes. Tell the user it's started, then ask exactly this one question and nothing else: \"If you want to create the artifact let me know once the forecast is generated.\" Do not add any other question, follow-up offer, or suggested next action/quick-reply chip (e.g. no CSV export or new-forecast suggestions) — that one question is the entire rest of your reply.",
          };
        },
      },
      {
        name: "getForecastResults",
        description:
          "Check on the forecast run started by runForecast, and once it's finished, fetch its completed results as plain data — this tool does NOT create the artifact itself, it only supplies the data. `results` has one entry per forecast that ran — this can be several SKUs (multiple SKU items in the plan, or a Product that expanded into all of its SKU variants). Once you have `results`, build a visualization artifact from them yourself (an HTML page preferred, or a PDF only if HTML genuinely doesn't fit) using your own creativity for layout and presentation (charts, tables, callout cards, sections, etc.) — cover every entry, never summarize only one SKU and drop or skim the rest — and use ONLY what this data actually shows, never inventing numbers, trends, or recommendations that aren't supported by it. Do not build an artifact for anything other than a completed forecast result. If the run isn't done yet, tell the user and try again shortly instead of guessing at results. This tool takes no arguments — call it with no input at all.",
        inputFields: [],
        outputFields: [
          {
            name: "success",
            type: "boolean",
            description: "Whether results were returned.",
            required: true,
          },
          {
            name: "ready",
            type: "boolean",
            description:
              "Whether the run has finished. False means still running — wait and call again.",
            required: true,
          },
          {
            name: "message",
            type: "string",
            description: "Human-readable status to relay to the user.",
            required: true,
          },
          {
            name: "results",
            type: "array",
            description:
              "One entry per completed forecast run. Present only when success and ready are both true.",
            required: false,
            innerFields: {
              type: "object",
              innerFields: [
                { name: "sku", type: "string", required: false, description: "SKU this run is for." },
                {
                  name: "forecast_by",
                  type: "string",
                  required: false,
                  description: "Scope the run was requested at: sku, product, category, or product type.",
                },
                { name: "horizon", type: "number", required: false, description: "Number of periods forecast." },
                { name: "type", type: "string", required: false, description: "Time bucket: Daily, Weekly, or Monthly." },
                {
                  name: "metrics",
                  type: "object",
                  required: false,
                  description:
                    "Computed metrics: total_on_hand, total_in_transit, total_quantity, avg_daily_demand, lead_time_days, demand_variability_over_lead_time, safety_stock_computed, safety_stock_override, safety_stock_used, reorder_point, recommended_order_qty, total_forecast_for_horizon, confidence_summary, reorder_by_date.",
                },
                { name: "product_name", type: "string", required: false, description: "Product the SKU belongs to." },
                { name: "run_at", type: "string", required: false, description: "When the forecast ran." },
                {
                  name: "forecast_adjusted",
                  type: "array",
                  required: false,
                  description:
                    "Per period: { date, predicted_qty, low, high } — low/high is the likely range. When the user added an event uplift: also event_uplift (added units, untested) and before_uplift (the method's own number).",
                },
                {
                  name: "sales_history",
                  type: "array",
                  required: false,
                  description: "Real sales per past period: [{ date, units }]. Estimated (Sales Fill) periods are NOT in here.",
                },
                {
                  name: "estimated_history",
                  type: "object",
                  required: false,
                  description:
                    "Present only when Sales Fill estimated months before the first sale: { periods, from, to, sources[] }. Always label these as estimates, never as sales.",
                },
                {
                  name: "method",
                  type: "object",
                  required: false,
                  description:
                    "How the forecast was made: { name, demand_type, tested_error_pct (units off per 100 sold on past periods), beat_same_as_last_year_pct, likely_range_level_pct, warnings[] }.",
                },
                {
                  name: "insight",
                  type: "array",
                  required: false,
                  description:
                    "The DropSkip Insight as plain-text points (method, reorder, stock, warehouse split …). Empty while insight_status is pending.",
                },
                { name: "insight_status", type: "string", required: false, description: "ready, pending or error." },
                {
                  name: "explanation",
                  type: "array",
                  required: false,
                  description: "The run's explanation as plain-text points.",
                },
                {
                  name: "warehouse_split",
                  type: "array",
                  required: false,
                  description: "Where to stock the forecast: [{ warehouse, share_pct, forecast_units }].",
                },
                {
                  name: "inventory_ledger",
                  type: "object",
                  required: false,
                  description:
                    "Month-by-month stock plan: { stock_as_of, lead_time_days, safety_stock, waiting_to_ship, months[] } — each month { month, kind (past/current/future), on_hand, sales, forecast, po_inbound, shortfall, recommended_order_qty, recommended_order_arrives, days_out_of_stock }.",
                },
              ],
            },
          },
        ],
        func: async () => {
          const state = runStateRef.current;
          if (state.status === "idle") {
            return {
              success: false,
              ready: false,
              message: "No forecast run has been started yet — call runForecast first.",
            };
          }
          if (state.status === "running") {
            return {
              success: false,
              ready: false,
              message:
                "The forecast is still running. Wait a little and call getForecastResults again — don't guess at results in the meantime.",
            };
          }
          if (state.status === "error") {
            return {
              success: false,
              ready: true,
              message: state.error || "The forecast run failed.",
            };
          }
          // status === "done"
          const ids = state.ids || [];
          if (!ids.length) {
            return {
              success: false,
              ready: true,
              message: "The run finished but no forecast results were found.",
            };
          }
          try {
            const docs = await Promise.all(
              ids.map((id) =>
                forecastDataService
                  .get(id)
                  .then((r) => r?.data)
                  .catch(() => null),
              ),
            );
            const results = docs.filter(Boolean).map(resultForAgent);
            if (!results.length) {
              return {
                success: false,
                ready: true,
                message: "The run finished but its results couldn't be loaded.",
              };
            }
            return {
              success: true,
              ready: true,
              message:
                "Forecast results are ready. Build the artifact from this data only.",
              results,
            };
          } catch (e) {
            console.error("getForecastResults tool failed", e);
            return {
              success: false,
              ready: true,
              message: e?.message || "Failed to load the forecast results.",
            };
          }
        },
      },
    ],
    [forecastDraftInputFields, onDraftForecast, onRunForecast],
  );

  // This expert isn't provisioned yet — config.js carries a placeholder
  // `id: ""` until it's filled in.
  const isConfigured = Boolean(config.kaman_expert_forecast_planner?.id);

  useEffect(() => {
    if (!isConfigured) return;
    const fetchKamanToken = async () => {
      try {
        const response = await kamanService.generateToken();
        if (response && response.token) {
          setKamanToken(response.token);
        }
      } catch (error) {
        console.error("Error fetching Kaman token:", error);
      }
    };

    fetchKamanToken();
  }, [isConfigured]);

  if (!isConfigured) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
          px: 3,
        }}
      >
        <Typography
          variant="h6"
          component="div"
          sx={{ color: "var(--text-primary)", fontWeight: "bold", mb: 1 }}
        >
          Forecast Planner isn't set up yet
        </Typography>
        <Typography
          variant="body2"
          component="div"
          sx={{
            color: "var(--text-tertiary)",
            opacity: 0.8,
            maxWidth: "440px",
          }}
        >
          Add its Kaman expert id and name to{" "}
          <code>kaman_expert_forecast_planner</code> in src/config.js.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
      {kamanToken && (
        <Box sx={{ height: "100%" }}>
          <Chatbot
            baseUrl={config.kaman_baseUrl}
            token={kamanToken}
            expertId={config.kaman_expert_forecast_planner.id}
            expertName={config.kaman_expert_forecast_planner.name}
            uiTools={uiTools}
            theme={{
              primaryColor: primary_color,
              secondaryColor: secondary_color,
              textColor: "var(--text-primary)",
              backgroundColor: "var(--bg-primary)",
              borderColor: "var(--border-color)",
              borderRadius: "8px",
              fontFamily: "Poppins",
            }}
            showAppsSelection={false}
            style={{
              width: "100%",
              height: chatHeight,
              textAlign: "left",
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default ForecastPlannerAgent;
